/**
 * SourceSelector — 參考來源選擇器
 * 支援：Google Drive | 本機目錄 | 上傳檔案 | 網站 URL
 * 所有來源可複選，儲存成 sources[] 陣列傳給父元件。
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Tabs, Button, Input, Space, Tag, Tree, Table, Upload,
  Spin, Alert, Tooltip, Typography, List, Badge, Divider, message,
  Modal,
} from 'antd';
import {
  FolderOutlined, FileOutlined, CloudOutlined, UploadOutlined,
  LinkOutlined, DeleteOutlined, CheckCircleOutlined, EyeOutlined,
  ReloadOutlined, GlobalOutlined, PlusOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const API = '/api/v1';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

// Supported file extensions badge
const EXT_COLOR = {
  '.pdf': 'red', '.docx': 'blue', '.xlsx': 'green', '.pptx': 'orange',
  '.txt': 'default', '.md': 'purple', '.csv': 'cyan', '.html': 'lime',
};

function SourceTag({ src, onRemove }) {
  const icons = { gdrive: <CloudOutlined />, local: <FolderOutlined />, upload: <UploadOutlined />, url: <GlobalOutlined /> };
  const colors = { gdrive: 'purple', local: 'blue', upload: 'cyan', url: 'green' };
  const label = src.name || src.url || src.path || src.id;
  return (
    <Tag
      icon={icons[src.type]}
      color={colors[src.type] || 'default'}
      closable
      onClose={() => onRemove(src)}
      style={{ marginBottom: 4, maxWidth: 220 }}
    >
      <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', verticalAlign: 'middle' }}>
        {label}
      </span>
    </Tag>
  );
}

// ── GDrive browser ────────────────────────────────────────────────────────────
function GDrivePanel({ selected, onAdd, onRemove }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stack, setStack] = useState([{ id: null, name: 'Google Drive 根目錄' }]);

  const load = useCallback(async (folderId) => {
    setLoading(true);
    try {
      const params = folderId ? `?folder_id=${folderId}` : '';
      const res = await fetch(`${API}/presentations/sources/gdrive/browse${params}`, { headers: authHeader() });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : (data.items || []));
    } catch (e) {
      message.error('GDrive 載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const currentFolder = stack[stack.length - 1];

  const enter = (item) => {
    setStack(s => [...s, { id: item.id, name: item.name }]);
    load(item.id);
  };
  const back = () => {
    const newStack = stack.slice(0, -1);
    setStack(newStack);
    load(newStack[newStack.length - 1].id);
  };

  const isSelected = (item) => selected.some(s => s.type === 'gdrive' && s.id === item.id);

  const toggle = (item) => {
    if (isSelected(item)) {
      onRemove({ type: 'gdrive', id: item.id });
    } else {
      onAdd({ type: 'gdrive', id: item.id, name: item.name, mime: item.mimeType });
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ marginBottom: 8 }}>
        {stack.map((s, i) => (
          <span key={i}>
            <Button type="link" size="small" onClick={() => { const ns = stack.slice(0, i + 1); setStack(ns); load(ns[ns.length - 1].id); }}>
              {s.name}
            </Button>
            {i < stack.length - 1 && <Text type="secondary">/</Text>}
          </span>
        ))}
      </div>
      {loading ? <Spin /> : (
        <List
          size="small"
          dataSource={items}
          renderItem={item => {
            const isDir = item.mimeType === 'application/vnd.google-apps.folder' || item.type === 'folder';
            const ext = item.name ? '.' + item.name.split('.').pop().toLowerCase() : '';
            const sel = isSelected(item);
            return (
              <List.Item
                style={{ cursor: 'pointer', background: sel ? '#f0f5ff' : undefined, padding: '6px 8px', borderRadius: 4 }}
                actions={[
                  isDir
                    ? <Button size="small" onClick={() => enter(item)}>開啟</Button>
                    : <Button size="small" type={sel ? 'primary' : 'default'} onClick={() => toggle(item)}>
                        {sel ? '已選' : '選擇'}
                      </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={isDir ? <FolderOutlined style={{ color: '#faad14', fontSize: 16 }} /> : <FileOutlined style={{ color: EXT_COLOR[ext] ? '#1890ff' : '#999', fontSize: 16 }} />}
                  title={<Text ellipsis style={{ maxWidth: 260 }}>{item.name}</Text>}
                  description={isDir ? '資料夾' : (item.size ? `${Math.round(item.size / 1024)} KB` : '')}
                />
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
}

// ── Local browser ─────────────────────────────────────────────────────────────
function LocalPanel({ selected, onAdd, onRemove }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stack, setStack] = useState(['']);

  const load = useCallback(async (path) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/presentations/sources/local/browse?path=${encodeURIComponent(path)}`, { headers: authHeader() });
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      message.error('本機目錄載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const currentPath = stack[stack.length - 1];

  const enter = (item) => {
    setStack(s => [...s, item.path]);
    load(item.path);
  };
  const back = () => {
    const ns = stack.slice(0, -1);
    setStack(ns);
    load(ns[ns.length - 1]);
  };

  const isSelected = (item) => selected.some(s => s.type === 'local' && s.path === item.path);

  const toggle = (item) => {
    if (isSelected(item)) {
      onRemove({ type: 'local', path: item.path });
    } else {
      onAdd({ type: 'local', path: item.path, name: item.name });
    }
  };

  const pathParts = ['根目錄', ...currentPath.split('/').filter(Boolean)];

  return (
    <div>
      <Alert message="/root/docker/spo/aippt" type="info" showIcon={false} style={{ marginBottom: 8, fontSize: 12 }} />
      {/* Breadcrumb */}
      <div style={{ marginBottom: 8 }}>
        {pathParts.map((part, i) => {
          const targetPath = i === 0 ? '' : currentPath.split('/').filter(Boolean).slice(0, i).join('/');
          return (
            <span key={i}>
              <Button type="link" size="small" onClick={() => { const ns = stack.slice(0, i); if (i === 0) { setStack(['']); load(''); } else { setStack([...ns, targetPath]); load(targetPath); } }}>
                {part}
              </Button>
              {i < pathParts.length - 1 && <Text type="secondary">/</Text>}
            </span>
          );
        })}
      </div>
      {loading ? <Spin /> : (
        <List
          size="small"
          dataSource={items}
          renderItem={item => {
            const sel = isSelected(item);
            const ext = item.ext || '';
            return (
              <List.Item
                style={{ cursor: 'pointer', background: sel ? '#f0f5ff' : undefined, padding: '6px 8px', borderRadius: 4 }}
                actions={[
                  item.type === 'dir'
                    ? <Button size="small" onClick={() => enter(item)}>開啟</Button>
                    : item.supported
                      ? <Button size="small" type={sel ? 'primary' : 'default'} onClick={() => toggle(item)}>{sel ? '已選' : '選擇'}</Button>
                      : <Text type="secondary" style={{ fontSize: 12 }}>不支援</Text>
                ]}
              >
                <List.Item.Meta
                  avatar={item.type === 'dir'
                    ? <FolderOutlined style={{ color: '#faad14', fontSize: 16 }} />
                    : <FileOutlined style={{ color: EXT_COLOR[ext] ? '#1890ff' : '#999', fontSize: 16 }} />
                  }
                  title={
                    <Space>
                      <Text ellipsis style={{ maxWidth: 220 }}>{item.name}</Text>
                      {ext && <Tag color={EXT_COLOR[ext] || 'default'} style={{ fontSize: 10, padding: '0 4px' }}>{ext}</Tag>}
                    </Space>
                  }
                  description={item.size ? `${Math.round(item.size / 1024)} KB` : ''}
                />
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
}

// ── Upload panel ──────────────────────────────────────────────────────────────
function UploadPanel({ selected, onAdd, onRemove }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async ({ file }) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/presentations/sources/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}` },
        body: form,
      });
      const data = await res.json();
      if (data.ok) {
        onAdd({ type: 'upload', path: data.path, name: data.name, size: data.size });
        message.success(`上傳成功：${data.name}`);
      } else {
        message.error(data.detail || '上傳失敗');
      }
    } catch (e) {
      message.error('上傳失敗');
    } finally {
      setUploading(false);
    }
    return false;
  };

  return (
    <div>
      <Upload.Dragger
        beforeUpload={() => false}
        customRequest={handleUpload}
        showUploadList={false}
        accept=".txt,.md,.pdf,.docx,.xlsx,.pptx,.csv,.html"
        style={{ marginBottom: 12 }}
      >
        <p className="ant-upload-drag-icon"><UploadOutlined /></p>
        <p>拖放或點擊上傳</p>
        <p style={{ color: '#999', fontSize: 12 }}>支援：PDF, DOCX, XLSX, PPTX, TXT, MD, CSV</p>
      </Upload.Dragger>
      {uploading && <Spin tip="上傳中..." />}
      {selected.filter(s => s.type === 'upload').length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>已上傳：</Text>
          {selected.filter(s => s.type === 'upload').map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <Text ellipsis style={{ maxWidth: 260, fontSize: 13 }}><FileOutlined /> {s.name}</Text>
              <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onRemove(s)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── URL panel ─────────────────────────────────────────────────────────────────
function UrlPanel({ selected, onAdd, onRemove }) {
  const [url, setUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchPreview = async () => {
    if (!url.startsWith('http')) return message.warning('請輸入完整的 URL（含 https://）');
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await fetch(`${API}/presentations/sources/fetch-url`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      setPreview(data);
    } catch (e) {
      message.error('URL 讀取失敗');
    } finally {
      setPreviewing(false);
    }
  };

  const addUrl = () => {
    if (!preview) return;
    const isDup = selected.some(s => s.type === 'url' && s.url === preview.url);
    if (isDup) return message.warning('此 URL 已加入');
    onAdd({ type: 'url', url: preview.url, name: preview.title || preview.url });
    setUrl('');
    setPreview(null);
  };

  return (
    <div>
      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <Input
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://example.com"
          onPressEnter={fetchPreview}
          prefix={<GlobalOutlined />}
        />
        <Button onClick={fetchPreview} loading={previewing}>預覽</Button>
      </Space.Compact>
      {preview && (
        <Alert
          message={<Text strong>{preview.title}</Text>}
          description={<Text type="secondary" style={{ fontSize: 12 }}>{preview.preview?.slice(0, 200)}...</Text>}
          type="success"
          action={<Button size="small" type="primary" onClick={addUrl} icon={<PlusOutlined />}>加入</Button>}
          style={{ marginBottom: 8 }}
        />
      )}
      {selected.filter(s => s.type === 'url').length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>已加入的網站：</Text>
          {selected.filter(s => s.type === 'url').map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <Text ellipsis style={{ maxWidth: 260, fontSize: 13 }}><GlobalOutlined /> {s.name}</Text>
              <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onRemove(s)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main SourceSelector ────────────────────────────────────────────────────────
export default function SourceSelector({ presentationId, initialSources = [], onSave, trigger }) {
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState(initialSources);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewModal, setPreviewModal] = useState(false);

  useEffect(() => { setSources(initialSources); }, [JSON.stringify(initialSources)]);

  const add = (src) => {
    setSources(prev => {
      const key = src.type === 'gdrive' ? s => s.id === src.id
        : src.type === 'local' ? s => s.path === src.path
        : src.type === 'upload' ? s => s.path === src.path
        : s => s.url === src.url;
      if (prev.some(key)) return prev;
      return [...prev, src];
    });
  };

  const remove = (src) => {
    setSources(prev => prev.filter(s => {
      if (src.type === 'gdrive') return s.id !== src.id || s.type !== 'gdrive';
      if (src.type === 'local') return s.path !== src.path || s.type !== 'local';
      if (src.type === 'upload') return s.path !== src.path || s.type !== 'upload';
      if (src.type === 'url') return s.url !== src.url || s.type !== 'url';
      return true;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/presentations/${presentationId}/sources`, {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify({ sources }),
      });
      onSave(sources);
      message.success(`已儲存 ${sources.length} 個參考來源`);
      setOpen(false);
    } catch (e) {
      message.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!sources.length) return message.warning('尚未選擇任何來源');
    setPreviewing(true);
    try {
      const res = await fetch(`${API}/presentations/${presentationId}/sources/preview`, { headers: authHeader() });
      const data = await res.json();
      setPreviewText(`已提取 ${data.chars?.toLocaleString()} 字元（${data.sources} 個來源）\n\n${data.context}`);
      setPreviewModal(true);
    } catch (e) {
      message.error('預覽失敗');
    } finally {
      setPreviewing(false);
    }
  };

  const typeCount = { gdrive: 0, local: 0, upload: 0, url: 0 };
  sources.forEach(s => { typeCount[s.type] = (typeCount[s.type] || 0) + 1; });

  return (
    <>
      {/* Trigger area */}
      <div onClick={() => setOpen(true)} style={{ cursor: 'pointer' }}>
        {trigger || (
          <Button icon={<FolderOutlined />} size="small">
            參考來源 {sources.length > 0 && <Badge count={sources.length} size="small" style={{ marginLeft: 4 }} />}
          </Button>
        )}
      </div>

      <Drawer
        title={
          <Space>
            <FolderOutlined />
            <span>參考來源設定</span>
            {sources.length > 0 && <Badge count={sources.length} />}
          </Space>
        }
        open={open}
        onClose={() => setOpen(false)}
        width={540}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={handlePreview} loading={previewing} icon={<EyeOutlined />}>
              預覽內容
            </Button>
            <Space>
              <Button onClick={() => setOpen(false)}>取消</Button>
              <Button type="primary" onClick={handleSave} loading={saving}>
                套用 {sources.length > 0 && `(${sources.length})`}
              </Button>
            </Space>
          </Space>
        }
      >
        {/* Selected sources summary */}
        {sources.length > 0 && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>已選來源（AI 將參考這些內容產生簡報）：</Text>
            <div style={{ marginTop: 6 }}>
              {sources.map((s, i) => <SourceTag key={i} src={s} onRemove={remove} />)}
            </div>
          </div>
        )}

        <Tabs defaultActiveKey="gdrive" size="small">
          <TabPane tab={<><CloudOutlined /> GDrive {typeCount.gdrive > 0 && <Badge count={typeCount.gdrive} size="small" />}</>} key="gdrive">
            <GDrivePanel selected={sources} onAdd={add} onRemove={remove} />
          </TabPane>
          <TabPane tab={<><FolderOutlined /> 本機 {typeCount.local > 0 && <Badge count={typeCount.local} size="small" />}</>} key="local">
            <LocalPanel selected={sources} onAdd={add} onRemove={remove} />
          </TabPane>
          <TabPane tab={<><UploadOutlined /> 上傳 {typeCount.upload > 0 && <Badge count={typeCount.upload} size="small" />}</>} key="upload">
            <UploadPanel selected={sources} onAdd={add} onRemove={remove} />
          </TabPane>
          <TabPane tab={<><GlobalOutlined /> 網站 {typeCount.url > 0 && <Badge count={typeCount.url} size="small" />}</>} key="url">
            <UrlPanel selected={sources} onAdd={add} onRemove={remove} />
          </TabPane>
        </Tabs>
      </Drawer>

      {/* Content preview modal */}
      <Modal
        title="AI 將讀取的來源內容預覽"
        open={previewModal}
        onCancel={() => setPreviewModal(false)}
        footer={<Button onClick={() => setPreviewModal(false)}>關閉</Button>}
        width={700}
      >
        <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 12, borderRadius: 6, maxHeight: '65vh', overflow: 'auto' }}>
          {previewText}
        </pre>
      </Modal>
    </>
  );
}
